import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/examples/jsm/utils/SkeletonUtils.js';

export interface LoadedGLB {
  scene: THREE.Group;
  animations: THREE.AnimationClip[];
}

const cache = new Map<string, Promise<LoadedGLB>>();
const loader = new GLTFLoader();

/** Loads (and caches) a GLB once — every S02 enemy instance shares the same parsed source via `instantiate()`. */
export function loadGLB(url: string): Promise<LoadedGLB> {
  let p = cache.get(url);
  if (!p) {
    p = loader.loadAsync(url).then((gltf) => ({ scene: gltf.scene, animations: gltf.animations }));
    cache.set(url, p);
  }
  return p;
}

/**
 * PLAN-CITY3 §0 L2 / R-1: every character-class GLB in this project is
 * authored facing +X in its own local space — verified per-asset by parsing
 * eye/nose/beak/tail node bounding boxes (city/CITY3-FINDINGS.md; re-verified
 * for the full roster during R-1's cast-wide audit: chr_runner, bos_ratking,
 * bos_gridlock, enm_rat, enm_trash_lobber, enm_pizza_rat, enm_pigeon,
 * enm_divepigeon all show their eye/nose/beak features at the max-X end and
 * tail/hindquarters at the min-X end — no exceptions found). The engine's
 * own yaw convention is +Z-forward (`Math.atan2(dirX, dirZ)`), so every
 * consumer that writes a computed facing angle onto a character's
 * `group.rotation.y` must add this offset first. Derivation: we need
 * Ry(θ)·(1,0,0) = (sin(facing), 0, cos(facing)), which gives θ = facing − π/2
 * (check: facing=π, i.e. facing away from the camera ⇒ θ=π/2 ⇒ local +X maps
 * to world (0,0,−1), into the screen — correct). Do NOT apply this inside
 * `instantiate()` itself — it is shared by every non-character asset too
 * (props, street kit, wearables), and a blind rotate-at-load would silently
 * break anything that isn't a directional character.
 */
export const CHARACTER_FORWARD_YAW_OFFSET = -Math.PI / 2;

export interface GLBInstance {
  group: THREE.Group;
  mixer: THREE.AnimationMixer;
  clips: Map<string, THREE.AnimationClip>;
  materials: THREE.Material[];
}

/**
 * Clones a loaded GLB into an independent scene graph AND independent
 * materials. `SkeletonUtils.clone()` clones the node/bone hierarchy but
 * still shares the SAME Material instances across every clone — flash-
 * tinting one rat (or the elite's darker recolor) would otherwise silently
 * repaint every other instance of the same asset, since they'd all point at
 * one shared THREE.Material object. Cloning materials here, once, per
 * instantiation is the fix (Classic's own gotcha #86 family: verify a
 * shared-resource assumption before trusting a per-instance effect).
 */
export function instantiate(source: LoadedGLB, tint: number | null = null): GLBInstance {
  const group = cloneSkeleton(source.scene) as THREE.Group;
  const materials: THREE.Material[] = [];
  const tintColor = tint !== null ? new THREE.Color(tint) : null;

  group.traverse((obj) => {
    const mesh = obj as THREE.Mesh;
    if (!mesh.isMesh) return;
    const applyOne = (m: THREE.Material): THREE.Material => {
      const cloned = m.clone();
      if (tintColor && cloned instanceof THREE.MeshStandardMaterial) {
        cloned.color.multiply(tintColor);
      }
      materials.push(cloned);
      return cloned;
    };
    mesh.material = Array.isArray(mesh.material) ? mesh.material.map(applyOne) : applyOne(mesh.material);
  });

  const mixer = new THREE.AnimationMixer(group);
  const clips = new Map<string, THREE.AnimationClip>();
  for (const clip of source.animations) clips.set(clip.name, clip);

  return { group, mixer, clips, materials };
}
